import React, { useState, useEffect } from "react";
import type { ViewsConfiguration } from '../../model/Configuration';
import { useViewsConfiguration } from '../../redux/Hooks/';
import { viewsConfigurationChangeAction } from "../../redux/Actions";
import { useDispatchKiosk } from "../../redux";
import { useLocale } from "@davi-ai/retorik-framework";
import translation from '../../translations/subMenuItems.json';
import commonTranslation from '../../translations/common.json';
import Button from "../Common/Button";
import Image from "./Background/Image";
import Video from "./Background/Video";
import Webcam from "./Background/Webcam";
import Underline from './Underline';
import tempViewsConfigurationChangeAction from "../../redux/Actions/Kiosk/tempViewsConfigurationChangeAction";

const BackgroundChoice = (props: { from: string }) => {
  const locale = useLocale();
  const dispatch = useDispatchKiosk();
  const viewsConfiguration: ViewsConfiguration = useViewsConfiguration();
  const [chosenBackground, setChosenBackground] = useState<string>(viewsConfiguration.views[props.from]?.background.style || '');
  const [inputs, setInputs] = useState<Array<MediaDeviceInfo>>([]);

  useEffect(() => {
    const checkMedia = async () => {
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        const videoInputs = devices.filter(({ kind }) => kind === "videoinput") || [];
        setInputs(videoInputs);
      });
    }

    checkMedia();
  }, []);

  const setWebcamRotation = (rotation: 0 | 90 | -90 | 180) => {
    dispatch(viewsConfigurationChangeAction({ ...viewsConfiguration, webcamRotation: rotation }));
    dispatch(tempViewsConfigurationChangeAction({ webcamRotation: rotation }));
  }

  return (
    <div className="w-full flex flex-col gap-4 mt-6">
      <div>
        {translation[locale]?.background.title || translation["fr-FR"].background.title}
      </div>
      <div className='w-full flex flex-col'>
        {/* Image background */}
        <div className='flex flex-row justify-between items-center my-1'>
          {translation[locale]?.background.image || translation["fr-FR"].background.image}
          {chosenBackground !== 'image' &&
            <Button 
              onClick={() => setChosenBackground('image')}
              label={commonTranslation[locale]?.choose || commonTranslation["fr-FR"].choose}
            >
              {commonTranslation[locale]?.choose || commonTranslation["fr-FR"].choose}
            </Button>
          }
        </div>
        {chosenBackground === 'image' && <Image from={props.from} />}

        <Underline />

        {/* Video background */}
        <div className='flex flex-row justify-between items-center my-1'>
          {translation[locale]?.background.video || translation["fr-FR"].background.video}
          {chosenBackground !== 'video' &&
            <Button
              onClick={() => setChosenBackground('video')}
              label={commonTranslation[locale]?.choose || commonTranslation["fr-FR"].choose}
            >
              {commonTranslation[locale]?.choose || commonTranslation["fr-FR"].choose}
            </Button>
          }
        </div>
        {chosenBackground === 'video' && <Video from={props.from} />}

        {/* Webcam background */}
        {inputs.length > 0 &&
          <React.Fragment>
            <Underline />

            <div className='flex flex-row justify-between items-center my-1'>
              {translation[locale]?.background.webcam || translation["fr-FR"].background.webcam}
              {chosenBackground !== 'webcam' &&
                <Button
                  onClick={() => setChosenBackground('webcam')}
                  label={commonTranslation[locale]?.choose || commonTranslation["fr-FR"].choose}
                >
                  {commonTranslation[locale]?.choose || commonTranslation["fr-FR"].choose}
                </Button>
              }
            </div>
            
            {chosenBackground === 'webcam' && <Webcam inputs={inputs} from={props.from} />}

            {props.from === 'home' &&
              <React.Fragment>
                {translation[locale]?.background.webcamrotation || translation["fr-FR"].background.webcamrotation}
                <div className='flex flex-row justify-between items-center my-1 flex-wrap'>
                  <Button label="0°" onClick={() => setWebcamRotation(0)} chosen={viewsConfiguration.webcamRotation === 0}>
                    0°
                  </Button>
                  <Button label="90°" onClick={() => setWebcamRotation(90)} chosen={viewsConfiguration.webcamRotation === 90}>
                    90°
                  </Button>
                  <Button label="180°" onClick={() => setWebcamRotation(180)} chosen={viewsConfiguration.webcamRotation === 180}>
                    180°
                  </Button>
                  <Button label="-90°" onClick={() => setWebcamRotation(-90)} chosen={viewsConfiguration.webcamRotation === -90}>
                    -90°
                  </Button>
                </div>
              </React.Fragment>
            }
          </React.Fragment>
        }
      </div>
    </div>
  );
};

export default BackgroundChoice;
